import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, ShieldCheck, User, Building2, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/core/auth/auth';
import { useToast } from '@/hooks/use-toast';
import {
  validatePassword,
  getStrengthColor,
  getStrengthBgColor,
  getAuthErrorMessage
} from '@/core/auth/passwordValidation';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';
import { supabase } from '@/integrations/supabase/client';
import { buildCallbackUrl } from '@/core/auth/redirect-utils';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code') || '';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState<'individual' | 'practice'>('individual');
  const [loading, setLoading] = useState(false);
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState('');

  // After a successful signUp under "Confirm email = ON", Supabase returns no
  // session — the user must click the verification link first. We swap the form
  // for a "check your email" panel so they aren't stuck staring at a static
  // toast wondering what happened.
  const [pendingVerification, setPendingVerification] = useState<string | null>(null);
  const [resendCooldownUntil, setResendCooldownUntil] = useState<number>(0);
  const [resending, setResending] = useState(false);
  const resendSecondsLeft = Math.max(0, Math.ceil((resendCooldownUntil - Date.now()) / 1000));

  const { user, loading: authLoading, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Redirect when auth is fully resolved (user exists and not loading)
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/app/billing/plans', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // No invite code → redirect to waitlist
  useEffect(() => {
    if (!inviteCode) {
      navigate('/waitlist', { replace: true });
    }
  }, [inviteCode, navigate]);

  // Validate invite code on mount
  useEffect(() => {
    if (!inviteCode) return;

    setCodeChecking(true);
    supabase.functions
      .invoke('waitlist-validate', {
        body: { invite_code: inviteCode },
      })
      .then(({ data, error }) => {
        if (error || !data?.valid) {
          setCodeValid(false);
          setCodeChecking(false);
          return;
        }

        setCodeValid(true);
        if (data?.email) {
          setInvitedEmail(data.email);
          setEmail(data.email);
        }
        setCodeChecking(false);
      })
      .catch(() => {
        setCodeValid(false);
        setCodeChecking(false);
      });
  }, [inviteCode]);

  // Real-time password validation
  const passwordValidation = useMemo(() => {
    if (!password) return null;
    return validatePassword(password, email);
  }, [password, email]);

  // Tick the resend cooldown so the button label counts down
  useEffect(() => {
    if (resendCooldownUntil <= Date.now()) return;
    const interval = setInterval(() => {
      if (Date.now() >= resendCooldownUntil) clearInterval(interval);
      setResendCooldownUntil((prev) => prev);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldownUntil]);

  const handleResend = async () => {
    if (!pendingVerification || resending || resendSecondsLeft > 0) return;
    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: pendingVerification,
        options: { emailRedirectTo: buildCallbackUrl() },
      });
      if (resendError) {
        toast({
          variant: 'destructive',
          title: 'Could not resend email',
          description: resendError.message || 'Please try again in a moment.',
        });
      } else {
        toast({
          title: 'Verification email sent',
          description: `A fresh link is on its way to ${pendingVerification}.`,
        });
        setResendCooldownUntil(Date.now() + 60_000);
      }
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const normalizedEmail = email.trim().toLowerCase();

    // Validate invite + email pairing server-side before account creation
    const { data: inviteValidation, error: inviteValidationError } = await supabase.functions.invoke('waitlist-validate', {
      body: {
        invite_code: inviteCode,
        email: normalizedEmail,
      },
    });

    if (inviteValidationError || !inviteValidation?.valid) {
      toast({
        variant: 'destructive',
        title: 'Invalid invite details',
        description: 'Invite code and email do not match an active invitation.',
      });
      setLoading(false);
      return;
    }

    // Validate password before submitting
    const validation = validatePassword(password, email);
    if (!validation.isValid) {
      toast({
        variant: 'destructive',
        title: 'Password requirements not met',
        description: validation.errors[0],
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = await signUp(normalizedEmail, password, fullName);

      if (error) {
        if (import.meta.env.DEV) {
          console.warn('[Signup] Sign-up error:', error);
        }
        toast({
          variant: 'destructive',
          title: 'Sign up failed',
          description: getAuthErrorMessage(error),
        });
        setLoading(false);
        return;
      }

      const { error: registerError } = await supabase.functions.invoke('waitlist-register', {
        body: {
          invite_code: inviteCode,
          email: normalizedEmail,
        },
      });

      if (registerError) {
        console.warn('[Signup] waitlist-register failed:', registerError);
      }

      // With "Confirm email = ON", Supabase signUp returns no session — the
      // user must verify first. Swap the form for a "check your email" panel.
      // If "Confirm email" ever gets flipped OFF, the useEffect on `user` will
      // navigate to billing and this state is just transient.
      setPendingVerification(normalizedEmail);
      setLoading(false);

      toast({
        title: 'Account created',
        description: 'Check your email for a verification link.',
      });
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[Signup] Unexpected sign-up error:', err);
      }
      toast({
        variant: 'destructive',
        title: 'Sign up failed',
        description: 'Something went wrong while creating your account. Please try again.',
      });
      setLoading(false);
    }
  };

  // Still checking code
  if (codeChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Validating invite code...</p>
        </div>
      </div>
    );
  }

  // Awaiting email verification (post-signup)
  if (pendingVerification) {
    const resendDisabled = resending || resendSecondsLeft > 0;
    const resendLabel = resending
      ? 'Sending...'
      : resendSecondsLeft > 0
        ? `Resend available in ${resendSecondsLeft}s`
        : 'Resend Verification Email';

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="relative z-10 w-full max-w-md">
          <div className="glass-strong rounded-2xl p-8 text-center">
            <div className="inline-flex items-center space-x-2 mb-6">
              <RadiologyLiveIcon size={48} />
            </div>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Check your email</h1>
            <p className="text-muted-foreground mb-6">
              We sent a verification link to <span className="font-medium text-foreground">{pendingVerification}</span>.
              Click it to activate your account, then sign in.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              Didn&apos;t get it? Check your spam folder, or resend below.
            </p>
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={resendDisabled}
                className="w-full"
              >
                <Mail className="mr-2 h-4 w-4" />
                {resendLabel}
              </Button>
              <Link to="/login">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Go to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid invite code
  if (codeValid === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="relative z-10 w-full max-w-md">
          <div className="glass-strong rounded-2xl p-8 text-center">
            <div className="inline-flex items-center space-x-2 mb-6">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Invalid Invite Code</h1>
            <p className="text-muted-foreground mb-6">
              This invite code is invalid or has already been used. Please check your invitation email or join our waitlist.
            </p>
            <div className="space-y-3">
              <Link to="/waitlist">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Join the Waitlist
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">

      <div className="relative z-10 w-full max-w-md">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        {/* Card */}
        <div className="glass-strong rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold">Create your account</h1>
            <p className="text-muted-foreground mt-2">You've been invited to join the RadiologyLive.ai beta</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Dr. Jane Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@hospital.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50"
                readOnly={!!invitedEmail}
              />
              {invitedEmail && (
                <p className="text-xs text-muted-foreground">This invite is tied to {invitedEmail}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Account Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setAccountType('individual')}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${
                    accountType === 'individual'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-muted/50 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Individual
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('practice')}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${
                    accountType === 'practice'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-muted/50 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  Practice
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-muted/50 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength indicator */}
              {passwordValidation && (
                <div className="space-y-2 mt-2">
                  {/* Strength bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${getStrengthBgColor(passwordValidation.strength)}`}
                        style={{ width: `${passwordValidation.score}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium capitalize ${getStrengthColor(passwordValidation.strength)}`}>
                      {passwordValidation.strength}
                    </span>
                  </div>

                  {/* Validation errors */}
                  {passwordValidation.errors.length > 0 && (
                    <p className="text-xs text-red-500">
                      {passwordValidation.errors[0]}
                    </p>
                  )}

                  {/* Success state */}
                  {passwordValidation.isValid && (
                    <p className="text-xs text-green-500 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      Password meets requirements
                    </p>
                  )}
                </div>
              )}

              {!password && (
                <p className="text-xs text-muted-foreground">Must be at least 12 characters</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading || (passwordValidation && !passwordValidation.isValid)}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
